import { useMemo } from 'react';

// Common weak passwords to check against
const COMMON_PASSWORDS = [
  'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', 'master',
  'dragon', '111111', 'baseball', 'iloveyou', 'trustno1', 'sunshine',
  'princess', 'welcome', 'admin', 'password1', 'password123', 'letmein',
  '1234567', '1234567890', 'qwerty123', 'admin123', 'root', 'toor'
];

export interface PasswordStrength {
  score: number; // 0-4
  label: 'Very Weak' | 'Weak' | 'Fair' | 'Strong' | 'Very Strong';
  color: string;
  checks: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
    notCommon: boolean;
  };
  isValid: boolean;
  feedback: string[];
}

export const usePasswordValidation = (password: string): PasswordStrength => {
  return useMemo(() => {
    const checks = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      notCommon: !COMMON_PASSWORDS.includes(password.toLowerCase()),
    };

    const feedback: string[] = [];
    if (!checks.minLength) feedback.push('At least 8 characters');
    if (!checks.hasUppercase) feedback.push('One uppercase letter');
    if (!checks.hasLowercase) feedback.push('One lowercase letter');
    if (!checks.hasNumber) feedback.push('One number');
    if (!checks.hasSpecial) feedback.push('One special character');
    if (!checks.notCommon) feedback.push('Avoid common passwords');

    // Calculate score based on checks passed
    let score = 0;
    if (checks.minLength) score++;
    if (checks.hasUppercase && checks.hasLowercase) score++;
    if (checks.hasNumber) score++;
    if (checks.hasSpecial) score++;
    if (password.length >= 12) score++; // Bonus for longer passwords

    // Cap at 4
    score = Math.min(score, 4);

    // Reduce score if common password
    if (!checks.notCommon) score = 0;

    const labels: PasswordStrength['label'][] = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
    const colors = [
      'bg-destructive',
      'bg-orange-500',
      'bg-yellow-500',
      'bg-emerald-500',
      'bg-green-600'
    ];

    // Password is valid if it meets minimum requirements
    const isValid = checks.minLength && checks.notCommon && 
      (checks.hasUppercase || checks.hasLowercase) && 
      (checks.hasNumber || checks.hasSpecial);

    return {
      score,
      label: labels[score],
      color: colors[score],
      checks,
      isValid,
      feedback,
    };
  }, [password]);
};
